import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  teamsAPI,
  usersAPI,
  Team,
  User,
  TeamMembership,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  PlusIcon,
  UsersIcon,
  UserPlusIcon,
  TrashIcon,
  XIcon,
  ShieldIcon,
  CheckIcon,
  PowerIcon,
} from '../icons';

// Premium line icons (1.5px stroke weight)
const Icons = {
  plus: <PlusIcon className="" width={18} height={18} strokeWidth={1.5} />,
  users: <UsersIcon className="" width={20} height={20} strokeWidth={1.5} />,
  userPlus: <UserPlusIcon className="" width={18} height={18} strokeWidth={1.5} />,
  trash: <TrashIcon className="" width={16} height={16} strokeWidth={1.5} />,
  x: <XIcon className="" width={18} height={18} strokeWidth={1.5} />,
  shield: <ShieldIcon className="" width={16} height={16} strokeWidth={1.5} />,
  check: <CheckIcon className="" width={16} height={16} strokeWidth={1.5} />,
  power: <PowerIcon className="" width={16} height={16} strokeWidth={1.5} />,
};

const TeamManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { confirm } = useConfirm();
  const isDark = theme === 'dark';
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
  });

  // Add member state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'member' | 'manager'>(
    'member',
  );

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-2'
    : 'block text-sm font-medium text-slate-700 mb-2';

  useEffect(() => {
    fetchTeams();
    fetchUsers();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data.teams || []);
    } catch (error: any) {
      setError(t('teams.failedToLoad'));
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
    } catch (error: any) {
      setError(t('users.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamDetails = async (teamId: number) => {
    try {
      const response = await teamsAPI.getById(teamId);
      setSelectedTeam(response.data.team);
    } catch (error: any) {
      setError(t('teams.failedToLoadDetails'));
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await teamsAPI.create(formData);
      setFormData({ name: '', description: '', active: true });
      setShowCreateForm(false);
      await fetchTeams();
    } catch (error: any) {
      setError(error.response?.data?.error || t('teams.failedToCreate'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTeam = async (teamId: number, updates: Partial<Team>) => {
    try {
      await teamsAPI.update(teamId, updates);
      await fetchTeams();
      if (selectedTeam?.id === teamId) {
        await fetchTeamDetails(teamId);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || t('teams.failedToUpdate'));
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    const confirmed = await confirm({
      title: t('teams.deleteTitle'),
      message: t('teams.confirmDelete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await teamsAPI.delete(teamId);
      await fetchTeams();
      if (selectedTeam?.id === teamId) {
        setSelectedTeam(null);
      }
    } catch (error: any) {
      setError(error.response?.data?.error || t('teams.failedToDelete'));
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam || !selectedUserId) return;

    try {
      await teamsAPI.addMember(
        selectedTeam.id,
        parseInt(selectedUserId),
        selectedRole,
      );
      setSelectedUserId('');
      setSelectedRole('member');
      setShowAddMemberModal(false);
      await fetchTeamDetails(selectedTeam.id);
      await fetchTeams();
    } catch (error: any) {
      setError(error.response?.data?.error || t('teams.failedToAddMember'));
    }
  };

  const handleRemoveMember = async (membershipId: number) => {
    if (!selectedTeam) return;
    const confirmed = await confirm({
      title: t('teams.removeMemberTitle'),
      message: t('teams.confirmRemoveMember'),
      confirmLabel: t('common.remove'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await teamsAPI.removeMember(selectedTeam.id, membershipId);
      await fetchTeamDetails(selectedTeam.id);
      await fetchTeams();
    } catch (error: any) {
      setError(error.response?.data?.error || t('teams.failedToRemoveMember'));
    }
  };

  const handleUpdateMemberRole = async (
    membershipId: number,
    newRole: 'member' | 'manager',
  ) => {
    if (!selectedTeam) return;

    try {
      await teamsAPI.updateMemberRole(selectedTeam.id, membershipId, newRole);
      await fetchTeamDetails(selectedTeam.id);
      await fetchTeams();
    } catch (error: any) {
      setError(error.response?.data?.error || t('teams.failedToUpdateRole'));
    }
  };

  const availableUsers = users.filter(
    (u) => !selectedTeam?.memberships?.some((m) => m.user.id === u.id),
  );

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="animate-pulse">
          <div
            className={`h-10 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-xl w-1/4 mb-6`}
          ></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-32 ${isDark ? 'bg-slate-900' : 'bg-slate-200'} rounded-2xl`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
              >
                <span
                  className={isDark ? 'text-violet-400' : 'text-violet-600'}
                >
                  {Icons.users}
                </span>
              </div>
              <h1
                className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('teams.title')}
              </h1>
            </div>
            <p
              className={`${isDark ? 'text-slate-400' : 'text-slate-600'} ml-14`}
            >
              {t('teams.subtitle')}
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 w-full sm:w-auto"
          >
            {Icons.plus}
            {t('teams.createTeam')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Teams List */}
        <div className="lg:col-span-1">
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('teams.teams')} ({teams.length})
            </h2>

            {/* Create Team Form */}
            {showCreateForm && (
              <form
                onSubmit={handleCreateTeam}
                className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-200'}`}
              >
                <h3
                  className={`font-semibold mb-3 ${isDark ? 'text-violet-300' : 'text-violet-900'}`}
                >
                  {t('teams.createNewTeam')}
                </h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder={t('teams.teamNamePlaceholder')}
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className={inputClass}
                    required
                  />
                  <textarea
                    placeholder={t('teams.descriptionPlaceholder')}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className={inputClass}
                    rows={2}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
                    >
                      {t('common.create')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className={`px-4 py-2 font-medium rounded-xl transition-colors ${
                        isDark
                          ? 'bg-slate-700 hover:bg-slate-600 text-white'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }`}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              </form>
            )}

            <div className="space-y-3">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                    selectedTeam?.id === team.id
                      ? isDark
                        ? 'bg-violet-500/20 border border-violet-500/40'
                        : 'bg-violet-50 border-2 border-violet-300'
                      : isDark
                        ? 'bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50'
                        : 'bg-slate-50 hover:bg-slate-100 border border-slate-200'
                  }`}
                  onClick={() => fetchTeamDetails(team.id)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {team.name}
                      </h3>
                      <p
                        className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                      >
                        {team.members_count} {t('teams.membersCount')}
                      </p>
                      {team.description && (
                        <p
                          className={`text-xs mt-1 truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                        >
                          {team.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`w-3 h-3 rounded-full ${team.active ? 'bg-emerald-500' : 'bg-rose-500'}`}
                      ></span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTeam(team.id);
                        }}
                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                      >
                        {Icons.trash}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Details */}
        <div className="lg:col-span-2">
          {selectedTeam ? (
            <div className={`${cardClass} rounded-2xl p-6`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div>
                  <h2
                    className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    {selectedTeam.name}
                  </h2>
                  {selectedTeam.description && (
                    <p
                      className={`mt-1 text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                    >
                      {selectedTeam.description}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <button
                    onClick={() => setShowAddMemberModal(true)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
                  >
                    {Icons.userPlus}
                    {t('teams.addMember')}
                  </button>
                  <button
                    onClick={() =>
                      handleUpdateTeam(selectedTeam.id, {
                        active: !selectedTeam.active,
                      })
                    }
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl shadow-lg transition-all duration-200 ${
                      selectedTeam.active
                        ? 'bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white shadow-rose-500/25'
                        : 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white shadow-emerald-500/25'
                    }`}
                  >
                    {Icons.power}
                    {selectedTeam.active
                      ? t('teams.deactivate')
                      : t('teams.activate')}
                  </button>
                </div>
              </div>

              {/* Team Members */}
              <div>
                <h3
                  className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('teams.teamMembers')} (
                  {selectedTeam.memberships?.length || 0})
                </h3>

                {selectedTeam.memberships &&
                selectedTeam.memberships.length > 0 ? (
                  <div className="grid gap-4">
                    {selectedTeam.memberships.map((membership) => (
                      <div
                        key={membership.id}
                        className={`p-4 rounded-xl ${
                          isDark
                            ? 'bg-slate-700/50 border border-slate-600/50'
                            : 'bg-slate-50 border border-slate-200'
                        }`}
                      >
                        <div className="flex items-center mb-3 sm:mb-0">
                          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-sm font-bold text-white">
                              {membership.user.full_name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .substring(0, 2)
                                .toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4
                              className={`font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                            >
                              {membership.user.full_name}
                            </h4>
                            <p
                              className={`text-sm truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                            >
                              {membership.user.email}
                            </p>
                            <p
                              className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                            >
                              {membership.user.role} • {t('teams.joined')}{' '}
                              {new Date(
                                membership.joined_at,
                              ).toLocaleDateString()}
                            </p>
                          </div>
                          {/* Desktop actions */}
                          <div className="hidden sm:flex items-center gap-2 ml-4">
                            <select
                              value={membership.role}
                              onChange={(e) =>
                                handleUpdateMemberRole(
                                  membership.id,
                                  e.target.value as 'member' | 'manager',
                                )
                              }
                              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                                isDark
                                  ? 'bg-slate-900 border-slate-600 text-white focus:border-violet-500'
                                  : 'bg-white border-slate-200 text-slate-900 focus:border-violet-500'
                              }`}
                            >
                              <option value="member">
                                {t('teams.roleMember')}
                              </option>
                              <option value="manager">
                                {t('teams.roleManager')}
                              </option>
                            </select>
                            <button
                              onClick={() => handleRemoveMember(membership.id)}
                              className={`p-2 rounded-xl transition-all ${
                                isDark
                                  ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                                  : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                              }`}
                            >
                              {Icons.trash}
                            </button>
                          </div>
                        </div>
                        {/* Mobile actions */}
                        <div className="flex sm:hidden items-center gap-2 pt-3 border-t border-slate-600/30">
                          <select
                            value={membership.role}
                            onChange={(e) =>
                              handleUpdateMemberRole(
                                membership.id,
                                e.target.value as 'member' | 'manager',
                              )
                            }
                            className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                              isDark
                                ? 'bg-slate-900 border-slate-600 text-white focus:border-violet-500'
                                : 'bg-white border-slate-200 text-slate-900 focus:border-violet-500'
                            }`}
                          >
                            <option value="member">
                              {t('teams.roleMember')}
                            </option>
                            <option value="manager">
                              {t('teams.roleManager')}
                            </option>
                          </select>
                          <button
                            onClick={() => handleRemoveMember(membership.id)}
                            className={`p-2 rounded-xl transition-all ${
                              isDark
                                ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                                : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                            }`}
                          >
                            {Icons.trash}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    <div
                      className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
                    >
                      <span
                        className={isDark ? 'text-slate-600' : 'text-slate-400'}
                      >
                        {Icons.users}
                      </span>
                    </div>
                    <p className="font-medium">{t('teams.noMembers')}</p>
                    <p className="text-sm mt-1">{t('teams.noMembersHint')}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={`${cardClass} rounded-2xl p-6`}>
              <div
                className={`text-center py-16 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                <div
                  className={`w-20 h-20 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
                >
                  <span
                    className={isDark ? 'text-slate-600' : 'text-slate-400'}
                  >
                    {Icons.users}
                  </span>
                </div>
                <h3
                  className={`text-lg font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  {t('teams.selectTeam')}
                </h3>
                <p>{t('teams.selectTeamHint')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`${cardClass} rounded-2xl p-6 w-full max-w-md mx-4`}>
            <div className="flex items-center justify-between mb-6">
              <h3
                className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('teams.addTeamMember')}
              </h3>
              <button
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedUserId('');
                  setSelectedRole('member');
                }}
                className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
              >
                {Icons.x}
              </button>
            </div>

            <form onSubmit={handleAddMember}>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>{t('teams.selectUser')}</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className={selectClass}
                    required
                  >
                    <option value="">{t('teams.chooseUser')}</option>
                    {availableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>{t('teams.roleInTeam')}</label>
                  <select
                    value={selectedRole}
                    onChange={(e) =>
                      setSelectedRole(e.target.value as 'member' | 'manager')
                    }
                    className={selectClass}
                  >
                    <option value="member">{t('teams.roleMember')}</option>
                    <option value="manager">{t('teams.roleManager')}</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddMemberModal(false);
                    setSelectedUserId('');
                    setSelectedRole('member');
                  }}
                  className={`px-5 py-2.5 font-medium rounded-xl transition-colors ${
                    isDark
                      ? 'bg-slate-700 hover:bg-slate-600 text-white'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!selectedUserId}
                  className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  {t('teams.addMember')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
